import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import fontBold from "../pdf/fonts/NotoSansTamil-Bold.ttf";
import fontRegular from "../pdf/fonts/NotoSansTamil-Regular.ttf";

// Register Tamil-supported custom fonts
Font.register({
  family: "NotoSansTamilBold",
  fonts: [{ src: fontBold, fontStyle: "normal", fontWeight: "bold" }],
});
Font.register({
  family: "NotoSansTamilRegular",
  fonts: [{ src: fontRegular, fontStyle: "normal", fontWeight: "normal" }],
});

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 7.5,
    fontFamily: "NotoSansTamilRegular",
    backgroundColor: "#f8fafc",
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "2px solid #3b82f6",
    paddingBottom: 10,
    marginBottom: 15,
  },
  companyName: {
    fontSize: 16,
    fontFamily: "NotoSansTamilBold",
    color: "#1e3a8a",
    letterSpacing: 0.5,
  },
  reportTitle: {
    fontSize: 10,
    color: "#475569",
    marginTop: 2,
    fontFamily: "NotoSansTamilBold",
  },
  headerMeta: {
    textAlign: "right",
    fontSize: 8,
    color: "#64748b",
  },
  summaryGrid: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
    gap: 8,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: "#ffffff",
    borderRadius: 6,
    padding: 8,
    border: "1px solid #e2e8f0",
  },
  summaryLabel: {
    fontSize: 7,
    color: "#64748b",
    marginBottom: 3,
    fontFamily: "NotoSansTamilBold",
    textTransform: "uppercase",
  },
  summaryValue: {
    fontSize: 11,
    color: "#0f172a",
    fontFamily: "NotoSansTamilBold",
  },
  table: {
    display: "table",
    width: "100%",
    borderRadius: 6,
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#1e293b",
    color: "#ffffff",
    fontFamily: "NotoSansTamilBold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#f1f5f9",
    alignItems: "center",
    minHeight: 22,
  },
  tableRowAlternating: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#f1f5f9",
    alignItems: "center",
    backgroundColor: "#f8fafc",
    minHeight: 22,
  },
  cell: {
    padding: 4,
    fontSize: 7,
    color: "#334155",
  },
  headerCell: {
    padding: 6,
    fontSize: 7.5,
    color: "#ffffff",
  },
  textCenter: {
    textAlign: "center",
  },
  textRight: {
    textAlign: "right",
  },
  totalRow: {
    flexDirection: "row",
    backgroundColor: "#e2e8f0",
    borderTopWidth: 1,
    borderColor: "#cbd5e1",
    alignItems: "center",
    minHeight: 24,
    fontFamily: "NotoSansTamilBold",
  },
  totalCell: {
    padding: 6,
    fontSize: 7.5,
    color: "#0f172a",
    fontFamily: "NotoSansTamilBold",
  },
  pageNumber: {
    position: "absolute",
    bottom: 20,
    left: 24,
    right: 24,
    textAlign: "center",
    fontSize: 8,
    color: "#94a3b8",
    borderTop: "1px solid #e2e8f0",
    paddingTop: 10,
  },
});

const DashBoardPDF = ({ data, aggregatedInterestData }) => {
  // Aggregate sum totals of key financial columns
  let totalPrincipal = 0;
  let totalWeightSum = 0;
  let totalNetWeightSum = 0;
  let totalAppraisalSum = 0;

  data.forEach((item) => {
    const jewelList = Array.isArray(item.jewel_product)
      ? item.jewel_product
      : typeof item.jewel_product === "string"
        ? JSON.parse(item.jewel_product || "[]")
        : [];
    const totalWeight = jewelList.reduce(
      (sum, jewel) => sum + parseFloat(jewel.weight || 0),
      0
    );
    const totalNetWeight = jewelList.reduce(
      (sum, jewel) => sum + parseFloat(jewel.net || 0),
      0
    );

    totalPrincipal += parseFloat(item.original_amount || 0);
    totalWeightSum += totalWeight;
    totalNetWeightSum += totalNetWeight;
    totalAppraisalSum +=
      parseFloat(item.original_amount || 0) +
      parseFloat(item.interest_payment_amount || 0);
  });

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Company and Document Header */}
        <View style={styles.headerContainer}>
          <View>
            <Text style={styles.companyName}>MINI MERCANTILE BANKERS</Text>
            <Text style={styles.reportTitle}>Jewelry Pawn Details Report</Text>
          </View>
          <View style={styles.headerMeta}>
            <Text>Date: {dayjs().format("DD-MM-YYYY HH:mm")}</Text>
            <Text>Records: {data.length}</Text>
          </View>
        </View>

        {/* Elegant Dashboard Summary Cards */}
        <View style={styles.summaryGrid}>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Total Loans</Text>
            <Text style={styles.summaryValue}>{data.length}</Text>
          </View>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Total Principal Amount</Text>
            <Text style={styles.summaryValue}>
              ₹{Math.round(totalPrincipal).toLocaleString("en-IN")}
            </Text>
          </View>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Total Net Weight</Text>
            <Text style={styles.summaryValue}>
              {totalNetWeightSum.toFixed(2)} g
            </Text>
          </View>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Total Appraisal Value</Text>
            <Text style={styles.summaryValue}>
              ₹{Math.round(totalAppraisalSum).toLocaleString("en-IN")}
            </Text>
          </View>
        </View>

        {/* Professional Styled Table */}
        <View style={styles.table}>
          {/* Table Header Row */}
          <View style={styles.tableHeader}>
            <Text style={[styles.cell, styles.headerCell, styles.textCenter, { flex: 0.4 }]}>SNo</Text>
            <Text style={[styles.cell, styles.headerCell, { flex: 0.9 }]}>Date</Text>
            <Text style={[styles.cell, styles.headerCell, { flex: 0.8 }]}>Loan No</Text>
            <Text style={[styles.cell, styles.headerCell, { flex: 1.4 }]}>Customer Name</Text>
            <Text style={[styles.cell, styles.headerCell, { flex: 1.0 }]}>Location</Text>
            <Text style={[styles.cell, styles.headerCell, { flex: 1.0 }]}>Mobile</Text>
            <Text style={[styles.cell, styles.headerCell, styles.textRight, { flex: 1.0 }]}>Principal (₹)</Text>
            <Text style={[styles.cell, styles.headerCell, styles.textCenter, { flex: 0.5 }]}>Rate</Text>
            <Text style={[styles.cell, styles.headerCell, { flex: 1.3 }]}>Items</Text>
            <Text style={[styles.cell, styles.headerCell, styles.textRight, { flex: 0.7 }]}>Gross Wt</Text>
            <Text style={[styles.cell, styles.headerCell, styles.textRight, { flex: 0.7 }]}>Net Wt</Text>
            <Text style={[styles.cell, styles.headerCell, { flex: 1.4 }]}>Int. O/S (Period / Amt)</Text>
            <Text style={[styles.cell, styles.headerCell, { flex: 1.4 }]}>Int. Paid (Period / Amt)</Text>
            <Text style={[styles.cell, styles.headerCell, styles.textRight, { flex: 1.1 }]}>Appraisal (₹)</Text>
            <Text style={[styles.cell, styles.headerCell, styles.textCenter, { flex: 0.8 }]}>Status</Text>
            <Text style={[styles.cell, styles.headerCell, styles.textCenter, { flex: 0.7 }]}>Overdue</Text>
          </View>

          {/* Table Body Rows */}
          {data.length === 0 ? (
            <View style={styles.tableRow}>
              <Text style={[styles.cell, { flex: 15, textAlign: "center", padding: 12 }]}>
                No pawn records matching the applied filters were found.
              </Text>
            </View>
          ) : (
            data.map((item, index) => {
              const jewelList = Array.isArray(item.jewel_product)
                ? item.jewel_product
                : typeof item.jewel_product === "string"
                  ? JSON.parse(item.jewel_product || "[]")
                  : [];
              const totalWeight = jewelList.reduce(
                (sum, jewel) => sum + parseFloat(jewel.weight || 0),
                0
              );
              const totalNetWeight = jewelList.reduce(
                (sum, jewel) => sum + parseFloat(jewel.net || 0),
                0
              );
              const pledgedItems = jewelList
                .map((jewel) => jewel.JewelName)
                .join(", ");

              const pawnDate = new Date(item.pawnjewelry_date);
              const currentDate = new Date();
              const yearDiff = currentDate.getFullYear() - pawnDate.getFullYear();
              const monthDiff = currentDate.getMonth() - pawnDate.getMonth();
              const totalMonths = yearDiff * 12 + monthDiff;
              const overdueStatus = totalMonths >= 4 ? "Notpaid" : "-";
              const isEven = index % 2 === 0;

              return (
                <View
                  key={item.id || index}
                  style={isEven ? styles.tableRow : styles.tableRowAlternating}
                >
                  <Text style={[styles.cell, styles.textCenter, { flex: 0.4 }]}>{index + 1}</Text>
                  <Text style={[styles.cell, { flex: 0.9 }]}>
                    {dayjs(item.pawnjewelry_date).format("DD-MM-YYYY")}
                  </Text>
                  <Text style={[styles.cell, { flex: 0.8 }]}>{item.receipt_no}</Text>
                  <Text style={[styles.cell, { flex: 1.4 }]}>{item.name}</Text>
                  <Text style={[styles.cell, { flex: 1.0 }]}>{item.place || "-"}</Text>
                  <Text style={[styles.cell, { flex: 1.0 }]}>{item.mobile_number}</Text>
                  <Text style={[styles.cell, styles.textRight, { flex: 1.0 }]}>
                    {Math.round(item.original_amount).toLocaleString("en-IN")}
                  </Text>
                  <Text style={[styles.cell, styles.textCenter, { flex: 0.5 }]}>
                    {item.interest_rate}%
                  </Text>
                  <Text style={[styles.cell, { flex: 1.3 }]}>{pledgedItems || "-"}</Text>
                  <Text style={[styles.cell, styles.textRight, { flex: 0.7 }]}>
                    {totalWeight.toFixed(2)}
                  </Text>
                  <Text style={[styles.cell, styles.textRight, { flex: 0.7 }]}>
                    {totalNetWeight.toFixed(2)}
                  </Text>
                  <Text style={[styles.cell, { flex: 1.4 }]}>
                    {item.interest_payment_period || "0"} (₹{Math.round(item.interest_payment_amount)})
                  </Text>
                  <Text style={[styles.cell, { flex: 1.4 }]}>
                    {aggregatedInterestData[item.receipt_no]
                      ?.interest_payment_periods || "N/A"}{" "}
                    (₹
                    {Math.round(
                      aggregatedInterestData[item.receipt_no]
                        ?.total_interest_income || 0
                    ).toLocaleString("en-IN")}
                    )
                  </Text>
                  <Text style={[styles.cell, styles.textRight, { flex: 1.1 }]}>
                    {Math.round(
                      parseFloat(item.original_amount || 0) +
                        parseFloat(item.interest_payment_amount || 0)
                    ).toLocaleString("en-IN")}
                  </Text>
                  <Text style={[styles.cell, styles.textCenter, { flex: 0.8 }]}>
                    {item.status === "நகை மீட்கபட்டது" ? "Recovered" : "Active"}
                  </Text>
                  <Text style={[styles.cell, styles.textCenter, { flex: 0.7 }]}>
                    {overdueStatus}
                  </Text>
                </View>
              );
            })
          )}

          {/* Sum Totals Footer Row */}
          {data.length > 0 && (
            <View style={styles.totalRow}>
              <Text style={[styles.cell, styles.totalCell, styles.textCenter, { flex: 0.4 }]}></Text>
              <Text style={[styles.cell, styles.totalCell, { flex: 0.9 }]}></Text>
              <Text style={[styles.cell, styles.totalCell, { flex: 0.8 }]}></Text>
              <Text style={[styles.cell, styles.totalCell, { flex: 1.4 }]}>Total Sum</Text>
              <Text style={[styles.cell, styles.totalCell, { flex: 1.0 }]}></Text>
              <Text style={[styles.cell, styles.totalCell, { flex: 1.0 }]}></Text>
              <Text style={[styles.cell, styles.totalCell, styles.textRight, { flex: 1.0 }]}>
                {Math.round(totalPrincipal).toLocaleString("en-IN")}
              </Text>
              <Text style={[styles.cell, styles.totalCell, styles.textCenter, { flex: 0.5 }]}></Text>
              <Text style={[styles.cell, styles.totalCell, { flex: 1.3 }]}></Text>
              <Text style={[styles.cell, styles.totalCell, styles.textRight, { flex: 0.7 }]}>
                {totalWeightSum.toFixed(2)}
              </Text>
              <Text style={[styles.cell, styles.totalCell, styles.textRight, { flex: 0.7 }]}>
                {totalNetWeightSum.toFixed(2)}
              </Text>
              <Text style={[styles.cell, styles.totalCell, { flex: 1.4 }]}></Text>
              <Text style={[styles.cell, styles.totalCell, { flex: 1.4 }]}></Text>
              <Text style={[styles.cell, styles.totalCell, styles.textRight, { flex: 1.1 }]}>
                {Math.round(totalAppraisalSum).toLocaleString("en-IN")}
              </Text>
              <Text style={[styles.cell, styles.totalCell, styles.textCenter, { flex: 0.8 }]}></Text>
              <Text style={[styles.cell, styles.totalCell, styles.textCenter, { flex: 0.7 }]}></Text>
            </View>
          )}
        </View>

        {/* Dynamic Footer with Page Numbers */}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages} - MINI MERCANTILE BANKERS`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default DashBoardPDF;
